import { useState } from "react";
import { Col, Row, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { SurveyManagementProps } from "@/features/survey/management/types";
import {
  SurveyAudience,
  SurveyStatus,
  SurveyType,
} from "@/shared/utils/constants";
import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";
import SurveyInvitesModal from "@/features/survey/management/components/SurveyInvitesModal";
import SurveyStatisticsTable from "@/features/survey/management/components/SurveyStatisticsTable";

const { Title, Text } = Typography;

const SurveyManagement = ({ survey }: SurveyManagementProps) => {
  const { t } = useTranslation();

  const [openSurveyInvitesModal, setOpenSurveyInvitesModal] = useState(false);
  const surveyStatus = SurveyStatus.getItemByValue(survey.status);

  return (
    <div className="page-layout">
      <div style={{ display: "flex" }}>
        <div>
          <BaseTag
            label={surveyStatus.label}
            color={surveyStatus.background}
            style={{ color: surveyStatus.color, fontWeight: "bold" }}
          />
        </div>
        <Title level={2} className="ms-3">
          {survey.title}
        </Title>
        {survey.status === SurveyStatus.ACTIVE.value && (
          <BaseButton
            className="ms-4"
            onClick={() => setOpenSurveyInvitesModal(true)}
            type={BUTTON_TYPES.LINK}
          >
            {survey.audience === SurveyAudience.ORGANIZATION.value
              ? t("survey.labels.inviteOrganizations")
              : t("survey.labels.inviteUsers")}
          </BaseButton>
        )}
      </div>
      <Row>
        <Col span={12} style={{ display: "flex" }}>
          <div>
            <Text>{t("survey.labels.startDate")}</Text>
            <div
              className="px-3 py-2"
              style={{
                backgroundColor: "rgba(231, 231, 231, 1)",
                border: "1px solid rgba(1, 1, 46, 0.134)",
              }}
            >
              {DateTime.fromISO(survey.startDate).toFormat("dd MMM yyyy HH:MM")}
            </div>
          </div>
          <div className="ms-3">
            <Text>{t("survey.labels.softDeadline")}</Text>
            <div
              className="px-3 py-2"
              style={{
                backgroundColor: "rgba(231, 231, 231, 1)",
                border: "1px solid rgba(1, 1, 46, 0.134)",
              }}
            >
              {survey?.softDeadline
                ? DateTime.fromISO(survey.softDeadline).toFormat(
                    "dd MMM yyyy HH:MM",
                  )
                : t("survey.labels.noSoftDeadline")}
            </div>
          </div>
          <div className="ms-3">
            <Text>{t("survey.labels.hardDeadline")}</Text>
            <div
              className="px-3 py-2"
              style={{
                backgroundColor: "rgba(231, 231, 231, 1)",
                border: "1px solid rgba(1, 1, 46, 0.134)",
              }}
            >
              {DateTime.fromISO(survey.hardDeadline).toFormat(
                "dd MMM yyyy HH:MM",
              )}
            </div>
          </div>
        </Col>
        <Col span={12} style={{ display: "flex" }}>
          <div className="ms-3">
            <div>
              <Text style={{ fontWeight: "bold" }}>
                {t("survey.labels.surveyType")}
              </Text>
            </div>
            <Text>{SurveyType.getItemByValue(survey.type).label}</Text>
          </div>
          <div className="ms-5">
            <div>
              <Text style={{ fontWeight: "bold" }}>
                {t("survey.labels.invited")}
              </Text>
            </div>
            <Text>{survey.invitesCount}</Text>
          </div>
          <div className="ms-5">
            <div>
              <Text style={{ fontWeight: "bold" }}>
                {t("survey.labels.responses")}
              </Text>
            </div>
            <Text>{survey.responsesCount}</Text>
          </div>
          <div className="ms-5">
            <div>
              <Text style={{ fontWeight: "bold" }}>
                {t("survey.labels.submittedSurveysCount")}
              </Text>
            </div>
            <Text>{survey.submittedSurveysCount}</Text>
          </div>
        </Col>
      </Row>

      <SurveyStatisticsTable
        defaultFilters={{ survey: survey.id }}
        columnNames={[
          "name",
          "isSurveyStarted",
          "answeredQuestionsCount",
          "attachedEvidenceCount",
          "isSubmissionRequired",
          "isSubmitted",
          "downloadReport",
        ]}
        filterNames={[
          "search",
          "isSurveyStarted",
          "isSubmissionRequired",
          "isSubmitted",
        ]}
      />

      <SurveyInvitesModal
        survey={survey}
        open={openSurveyInvitesModal}
        setOpen={setOpenSurveyInvitesModal}
      />
    </div>
  );
};

export default SurveyManagement;
